import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { AuthProvider } from './auth.provider';
import { AuthClientKey, IOidcClientConfig } from '@web/dtos';
import { Router } from '@angular/router';

export class KeycloakAuthProvider implements AuthProvider {
  private static readonly SHOP_LOGOUT_RETURN_URL_KEY: string =
    'auth.shop.logoutReturnUrl';

  private initializationPromise: Promise<void> | null = null;
  private tokenWatcherInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private oauthService: OAuthService,
    private config: IOidcClientConfig,
    private router: Router,
    private clientKey: AuthClientKey
  ) {
    this.configure();
  }

  private configure(): void {
    const authConfig: AuthConfig = {
      issuer: this.config.issuer,
      clientId: this.config.clientId,
      redirectUri: this.config.redirectUri,
      scope: this.config.scope,
      responseType: this.config.responseType ?? 'code',
      requireHttps: this.config.requireHttps ?? false,
      useSilentRefresh: false,
      silentRefreshRedirectUri: undefined,
      sessionChecksEnabled: true,
      showDebugInformation: false,
      clearHashAfterLogin: true,
    };

    this.oauthService.configure(authConfig);

    this.oauthService.events.subscribe(event => {
      if (event.type === 'token_received') {
        this.startTokenWatcher();
      }
      if (
        event.type === 'token_error' ||
        event.type === 'discovery_document_load_error'
      ) {
        console.error('[Auth] Error:', event);
      }
    });

    this.oauthService.setupAutomaticSilentRefresh();
  }

  private isOnRedirectUri(): boolean {
    const currentPath = window.location.origin + window.location.pathname;
    const redirectPath = this.config.redirectUri
      .split('?')[0]
      .replace(/\/$/, '');
    const currentNormalized = currentPath.replace(/\/$/, '');

    return currentNormalized === redirectPath;
  }

  initialize(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = Promise.resolve()
      .then(async () => {
        if (this.isOnRedirectUri()) {
          await this.oauthService.loadDiscoveryDocumentAndTryLogin();
          await this.restoreShopReturnUrl();
          return;
        }
        await this.oauthService.loadDiscoveryDocument();
      })
      .catch(err => {
        this.initializationPromise = null;
        console.error(
          `Authentication initialization failed for client: ${this.config.clientId}`,
          err
        );
        throw err;
      });
    return this.initializationPromise;
  }

  private startTokenWatcher(): void {
    this.stopTokenWatcher();
    this.tokenWatcherInterval = setInterval(() => {
      if (!this.oauthService.hasValidAccessToken()) {
        this.logout();
      }
    }, 30_000);
  }

  private stopTokenWatcher(): void {
    if (this.tokenWatcherInterval !== null) {
      clearInterval(this.tokenWatcherInterval);
      this.tokenWatcherInterval = null;
    }
  }

  login(returnUrl?: string): void {
    this.initialize()
      .then(() => {
        if (this.isShopClient()) {
          this.oauthService.initCodeFlow(returnUrl ?? this.router.url);
          return;
        }

        this.oauthService.initCodeFlow();
      })
      .catch(err => {
        console.error('Login fehlgeschlagen – Discovery nicht verfügbar', err);
      });
  }

  logout(): void {
    this.stopTokenWatcher();

    if (this.isShopClient()) {
      this.saveShopLogoutReturnUrl();
      this.oauthService.logOut();
      return;
    }

    this.oauthService.logOut();
    this.router.navigate(['/']);
  }

  private isShopClient(): boolean {
    return this.clientKey === 'shop';
  }

  private async restoreShopReturnUrl(): Promise<void> {
    if (!this.isShopClient()) {
      return;
    }

    const loginReturnUrl = this.decodeLoginReturnUrl(this.oauthService.state);
    const logoutReturnUrl = this.takeShopLogoutReturnUrl();
    const returnUrl = loginReturnUrl || logoutReturnUrl;

    if (this.isSafeInternalUrl(returnUrl) && returnUrl !== this.router.url) {
      await this.router.navigateByUrl(returnUrl);
    }
  }

  private saveShopLogoutReturnUrl(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const returnUrl = this.isPublicShopUrl(this.router.url)
      ? this.router.url
      : '/';
    sessionStorage.setItem(
      KeycloakAuthProvider.SHOP_LOGOUT_RETURN_URL_KEY,
      returnUrl
    );
  }

  private takeShopLogoutReturnUrl(): string | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    const returnUrl = sessionStorage.getItem(
      KeycloakAuthProvider.SHOP_LOGOUT_RETURN_URL_KEY
    );
    sessionStorage.removeItem(KeycloakAuthProvider.SHOP_LOGOUT_RETURN_URL_KEY);
    return returnUrl;
  }

  private isPublicShopUrl(url: string): boolean {
    return !/^\/account(?:[/?#]|$)/.test(url);
  }

  private decodeLoginReturnUrl(state: string | undefined): string | null {
    if (!state) {
      return null;
    }

    try {
      return decodeURIComponent(state);
    } catch {
      return null;
    }
  }

  private isSafeInternalUrl(url: string | null | undefined): url is string {
    return !!url && url.startsWith('/') && !url.startsWith('//');
  }

  isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken() ?? '';
  }

  destroy(): void {
    this.stopTokenWatcher();
  }
}
